import { useEffect, useRef } from 'react';

const EDITABLE_TAGS = new Set(['INPUT', 'TEXTAREA', 'SELECT']);

function isEditableTarget(target: EventTarget | null): boolean {
  if (!target || !(target instanceof HTMLElement)) {
    return false;
  }
  if (EDITABLE_TAGS.has(target.tagName)) {
    return true;
  }
  if (target.isContentEditable) {
    return true;
  }
  return false;
}

export type ShortcutBinding = {
  key: string;
  handler: () => void;
  ctrlOrMeta?: boolean;
  shift?: boolean;
  alt?: boolean;
  preventDefault?: boolean;
};

export type ShortcutOptions = {
  enabled?: boolean;
};

export function useGlobalShortcuts(
  shortcuts: ShortcutBinding[],
  options: ShortcutOptions = {}
): void {
  const { enabled = true } = options;
  const shortcutsRef = useRef(shortcuts);

  useEffect(() => {
    shortcutsRef.current = shortcuts;
  }, [shortcuts]);

  useEffect(() => {
    if (!enabled) {
      return undefined;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.repeat) {
        return;
      }

      if (isEditableTarget(event.target)) {
        return;
      }

      const normalizedKey = event.key.toLowerCase();

      for (const shortcut of shortcutsRef.current) {
        if (normalizedKey !== shortcut.key.toLowerCase()) {
          continue;
        }
        if (shortcut.ctrlOrMeta && !(event.metaKey || event.ctrlKey)) {
          continue;
        }
        if (!shortcut.ctrlOrMeta && (event.metaKey || event.ctrlKey)) {
          continue;
        }
        if (shortcut.shift && !event.shiftKey) {
          continue;
        }
        if (!shortcut.shift && event.shiftKey) {
          continue;
        }
        if (shortcut.alt && !event.altKey) {
          continue;
        }
        if (!shortcut.alt && event.altKey) {
          continue;
        }

        if (shortcut.preventDefault ?? true) {
          event.preventDefault();
        }
        shortcut.handler();
        break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [enabled]);
}
